<template>
  <div
    :class="{ highlighted: isHighlighted }"
    class="participant"
  >
    <div class="wrap-avatar">
      <router-link
        :to="{ name: 'Profile', params: { id: String(participant.id) } }"
        class="user-link"
      >
        <span
          v-if="participant.randId"
          class="user-order"
        >
          {{ participant.randId }}
        </span>
        <BKBadge
          v-if="isParticipantCheckedIn || !isParticipantConfirmed"
          :color="getBadgeColor"
          bordered
          bottom
          offset-x="13"
        >
          <template #badge>
            <span>
              <v-icon v-if="isParticipantCheckedIn">{{ mdiCheck }}</v-icon>
              <v-icon v-if="!isParticipantConfirmed">{{ mdiHelp }}</v-icon>
            </span>
          </template>
          <v-avatar
            class="d-inline-block"
            size="32"
          >
            <v-img :src="getAvatar(participant.avatar, 64)" />
          </v-avatar>
        </BKBadge>
        <v-avatar
          v-else
          size="32"
        >
          <img
            :alt="participant.dancerName"
            :src="getAvatar(participant.avatar, 64)"
          />
        </v-avatar>
        <span class="participant-name">{{ participant.dancerName }}</span>
      </router-link>

      <v-btn
        v-if="event.permissions.canEdit"
        icon
        type="button"
        @click="isShowActions = !isShowActions"
      >
        <img
          v-if="!isShowActions"
          alt="open"
          src="/img/ic_Menu-Elipses-Horizontal.svg"
        />
        <img
          v-else
          alt="close"
          src="/img/service-worker.svg"
        />
      </v-btn>

      <template v-if="isShowActions">
        <div class="actions-container mt-5">
          <slot name="actions" />
        </div>
      </template>
    </div>
    <div
      v-if="'studios' in participant && !!participant.studios?.length"
      class="wrap-studio"
    >
      <router-link
        v-for="studio in participant.studios"
        :key="studio.id"
        :to="{ name: 'Studio', params: { id: String(studio.id) } }"
        class="studio-link"
      >
        <v-avatar
          size="24"
          class="studio-image"
        >
          <img
            :alt="studio.name"
            :src="getStudioImage(studio.image, 48)"
          />
        </v-avatar>
        <span class="studio-name">{{ studio.name }}</span>
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import BKBadge from '@/components/AppShared/BKBadge.vue'
import { mdiCheck, mdiHelp } from '@mdi/js'
import { useImage } from '@/shared/composables/image'
import { BattleParticipant } from '@/enitites/battle/model/types'
import { IEvent } from '@/enitites/event'

const props = defineProps<{
  participant: BattleParticipant
  isHighlighted?: boolean
  event: IEvent
}>()

const { getAvatar, getStudioImage } = useImage()
const isShowActions = ref(false)

const isParticipantConfirmed = computed(() => {
  const date = props.participant.confirmedAt
  return date instanceof Date ? date.getFullYear() > 2000 : new Date(date).getFullYear() > 2000
})

const isParticipantCheckedIn = computed(() => {
  const date = props.participant.checkedInAt
  return date instanceof Date ? date.getFullYear() > 2000 : new Date(date).getFullYear() > 2000
})

const getBadgeColor = computed(() => {
  let color
  if (isParticipantCheckedIn.value) {
    color = 'var(--color-green)'
  }

  if (!isParticipantConfirmed.value) {
    color = 'var(--color-orange-500)'
  }

  return color
})
</script>

<style lang="scss" scoped>
@import '~@/assets/style/mixins';

.participant {
  padding: 12px 12px 16px;
  border-bottom: 1px solid var(--color-grey-600);

  &:last-child {
    border-bottom: none;
  }

  &.highlighted {
    border: 2px solid var(--color-orange-500);
  }
}

.wrap-avatar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
}

.user-link {
  display: flex;
  gap: 8px;
  align-items: center;
  color: var(--color-dark-800) !important;
}

.actions-container {
  display: grid;
  flex-basis: 100%;
  gap: 20px;
}

.wrap-studio {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-top: 10px;

  .studio-link {
    display: flex;
    gap: 4px;
    align-items: center;
    color: var(--color-grey-300) !important;
    font-size: 14px;
  }

  .studio-image {
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    border: 1px solid var(--color-grey-500);
    border-radius: 999px;
    overflow: hidden;
  }
}
</style>
